import dayjs from 'dayjs'
import type {Dayjs} from 'dayjs'
import type {FollowUpAtualizacaoItemRequest, FollowUpItemOrdemCompraResponse} from '../../types'

export const TAMANHO_MINIMO_JUSTIFICATIVA = 20

export const CASAS_DECIMAIS_PRECO_COMPRA = 4

export type ErroValidacaoItem = 'justificativa-obrigatoria' | 'justificativa-curta'

export interface ItemOrdemCompraEditado {
    precoCompra: number | null;
    quantidade: number | null;
    justificativa: string;
    previsaoEntrega: Dayjs | null;
}

export type EdicaoItensOrdemCompra = Record<number, ItemOrdemCompraEditado>

const FORMATO_DATA_ENVIO = 'YYYY-MM-DDTHH:mm:ss'

// Preço vem do backend como BigDecimal e volta do input como number: a comparação
// direta acusaria alteração por ruído de ponto flutuante (12.34 !== 12.340000000000001).
const TOLERANCIA_NUMERICA = 1e-9

function saoNumerosIguais(primeiro: number | null, segundo: number | null): boolean {
    if (primeiro === null || segundo === null) return primeiro === segundo

    return Math.abs(primeiro - segundo) < TOLERANCIA_NUMERICA
}

export function criarEdicaoDoItem(item: FollowUpItemOrdemCompraResponse): ItemOrdemCompraEditado {
    return {
        precoCompra: item.precoCompra,
        quantidade: item.quantidade,
        justificativa: item.justificativa ?? '',
        previsaoEntrega: item.previsaoEntrega === null ? null : dayjs(item.previsaoEntrega),
    }
}

export function criarEdicaoInicial(itens: FollowUpItemOrdemCompraResponse[]): EdicaoItensOrdemCompra {
    const edicoes: EdicaoItensOrdemCompra = {}

    for (const item of itens) {
        edicoes[item.idPreCompraItem] = criarEdicaoDoItem(item)
    }

    return edicoes
}

export function isPrecoCompraAlterado(original: FollowUpItemOrdemCompraResponse, edicao: ItemOrdemCompraEditado): boolean {
    return !saoNumerosIguais(original.precoCompra, edicao.precoCompra)
}

export function isQuantidadeAlterada(original: FollowUpItemOrdemCompraResponse, edicao: ItemOrdemCompraEditado): boolean {
    return !saoNumerosIguais(original.quantidade, edicao.quantidade)
}

export function isJustificativaObrigatoria(original: FollowUpItemOrdemCompraResponse, edicao: ItemOrdemCompraEditado): boolean {
    return isPrecoCompraAlterado(original, edicao) || isQuantidadeAlterada(original, edicao)
}

export function validarItem(
    original: FollowUpItemOrdemCompraResponse,
    edicao: ItemOrdemCompraEditado,
): ErroValidacaoItem | null {
    if (!isJustificativaObrigatoria(original, edicao)) return null

    const justificativa = edicao.justificativa.trim()
    if (justificativa.length === 0) return 'justificativa-obrigatoria'

    return justificativa.length < TAMANHO_MINIMO_JUSTIFICATIVA ? 'justificativa-curta' : null
}

export function validarItens(
    itens: FollowUpItemOrdemCompraResponse[],
    edicoes: EdicaoItensOrdemCompra,
): Record<number, ErroValidacaoItem> {
    const erros: Record<number, ErroValidacaoItem> = {}

    for (const item of itens) {
        const erro = validarItem(item, edicoes[item.idPreCompraItem] ?? criarEdicaoDoItem(item))
        if (erro !== null) erros[item.idPreCompraItem] = erro
    }

    return erros
}

export function calcularValorTotal(edicao: ItemOrdemCompraEditado): number | null {
    if (edicao.precoCompra === null || edicao.quantidade === null) return null

    return edicao.precoCompra * edicao.quantidade
}

export function montarAtualizacaoDosItens(
    itens: FollowUpItemOrdemCompraResponse[],
    edicoes: EdicaoItensOrdemCompra,
): FollowUpAtualizacaoItemRequest[] {
    return itens.map(item => {
        const edicao = edicoes[item.idPreCompraItem] ?? criarEdicaoDoItem(item)
        const justificativa = edicao.justificativa.trim()

        return {
            id: item.idPreCompraItem,
            precoCompra: edicao.precoCompra,
            quantidade: edicao.quantidade,
            justificativa: justificativa.length === 0 ? null : justificativa,
            previsaoEntrega: edicao.previsaoEntrega === null ? null : edicao.previsaoEntrega.format(FORMATO_DATA_ENVIO),
        }
    })
}
